import {
	STEP_GROUP_CLASS,
	STEP_OPEN_CLASS,
} from './sidebar-constants.js';
import {
	readPrimaryAccordionState,
	writePrimaryAccordionState,
} from './sidebar-runtime-store.js';
import {
	resolvePrimaryStepPanel,
	resolvePrimaryToggleButton,
	resolveRenderedCurrentPrimaryTargetPage,
	resolveRenderedPrimaryTargetPage,
} from './sidebar-step-render.js';
import { toPositiveInteger } from './sidebar-utils.js';

const accordionAnimationStates = new WeakMap();

/**
* Responsibility:
* - Return the reusable animation bookkeeping record for one rendered accordion panel.
*
* Touches:
* - Reads and writes the module-level accordion animation state registry.
*
* Guarantees:
* - Provides stable cancellation slots for the active frame and transition-end listener.
*/
function getAccordionAnimationState(panel) {
	if (!accordionAnimationStates.has(panel)) {
		accordionAnimationStates.set(panel, {
			frameId: 0,
			transitionEndHandler: null,
		});
	}

	return accordionAnimationStates.get(panel);
}

/**
* Responsibility:
* - Cancel any pending accordion height animation work before a new transition begins.
*
* Touches:
* - Cancels queued animation frames and detaches transition-end listeners from the current panel.
*
* Guarantees:
* - Leaves the panel animation record reset to an idle state.
*/
function cancelPendingAccordionAnimation(panel, animationState) {
	if (animationState.frameId !== 0) {
		cancelAnimationFrame(animationState.frameId);
		animationState.frameId = 0;
	}

	if (typeof animationState.transitionEndHandler === 'function') {
		panel.removeEventListener('transitionend', animationState.transitionEndHandler);
		animationState.transitionEndHandler = null;
	}
}

/**
* Responsibility:
* - Expand one rendered grouped-step substep panel with a measured-height transition.
*
* Touches:
* - Unhides the panel, writes temporary inline `height`, and updates the primary row `is-open` state.
*
* Guarantees:
* - Restores the panel to natural auto height after the transition completes.
*/
function openPrimaryStepPanel(item, panel, toggleButton) {
	if (!(item instanceof HTMLElement) || !(panel instanceof HTMLElement)) {
		return;
	}

	const animationState = getAccordionAnimationState(panel);
	cancelPendingAccordionAnimation(panel, animationState);

	const startHeight = panel.hidden ? 0 : panel.getBoundingClientRect().height;
	panel.hidden = false;
	item.classList.add(STEP_OPEN_CLASS);
	if (toggleButton instanceof HTMLButtonElement) {
		toggleButton.setAttribute('aria-expanded', 'true');
	}

	panel.style.height = `${startHeight}px`;
	const endHeight = panel.scrollHeight;
	if (Math.abs(endHeight - startHeight) < 1) {
		panel.style.height = '';
		return;
	}

	const handleTransitionEnd = (event) => {
		if (event.target !== panel || event.propertyName !== 'height') {
			return;
		}

		panel.style.height = '';
		panel.removeEventListener('transitionend', handleTransitionEnd);
		animationState.transitionEndHandler = null;
	};

	animationState.transitionEndHandler = handleTransitionEnd;
	panel.addEventListener('transitionend', handleTransitionEnd);
	animationState.frameId = requestAnimationFrame(() => {
		animationState.frameId = 0;
		panel.style.height = `${endHeight}px`;
	});
}

/**
* Responsibility:
* - Collapse one rendered grouped-step substep panel with a measured-height transition.
*
* Touches:
* - Measures the current panel height, animates it to zero, and hides the panel after the transition.
*
* Guarantees:
* - Leaves the panel fully hidden with clean inline styles after completion.
*/
function closePrimaryStepPanel(item, panel, toggleButton) {
	if (!(item instanceof HTMLElement) || !(panel instanceof HTMLElement)) {
		return;
	}

	const animationState = getAccordionAnimationState(panel);
	cancelPendingAccordionAnimation(panel, animationState);

	const startHeight = panel.getBoundingClientRect().height;
	item.classList.remove(STEP_OPEN_CLASS);
	if (toggleButton instanceof HTMLButtonElement) {
		toggleButton.setAttribute('aria-expanded', 'false');
	}
	panel.style.height = `${startHeight}px`;

	if (startHeight < 1) {
		panel.hidden = true;
		panel.style.height = '';
		return;
	}

	const handleTransitionEnd = (event) => {
		if (event.target !== panel || event.propertyName !== 'height') {
			return;
		}

		panel.hidden = true;
		panel.style.height = '';
		panel.removeEventListener('transitionend', handleTransitionEnd);
		animationState.transitionEndHandler = null;
	};

	animationState.transitionEndHandler = handleTransitionEnd;
	panel.addEventListener('transitionend', handleTransitionEnd);
	animationState.frameId = requestAnimationFrame(() => {
		animationState.frameId = 0;
		panel.style.height = '0px';
	});
}

/**
* Responsibility:
* - Apply one-open-at-a-time expansion state to all rendered grouped primary-step panels.
*
* Touches:
* - Reads the rendered primary items under the sidebar list and updates their open/closed state.
*
* Guarantees:
* - Exactly one expandable primary step stays open when a target page is provided.
*/
export function applyPrimaryStepExpansion(listElement, formId, expandedTargetPage, { animate = true } = {}) {
	if (!(listElement instanceof HTMLElement) || toPositiveInteger(formId) <= 0) {
		return;
	}

	const normalizedExpandedTarget = toPositiveInteger(expandedTargetPage);
	Array.from(listElement.children).forEach((child) => {
		if (!(child instanceof HTMLElement) || !child.classList.contains(STEP_GROUP_CLASS)) {
			return;
		}

		const targetPage = resolveRenderedPrimaryTargetPage(child);
		const panel = resolvePrimaryStepPanel(child);
		const toggleButton = resolvePrimaryToggleButton(child);
		if (!(panel instanceof HTMLElement)) {
			return;
		}

		const shouldOpen = normalizedExpandedTarget > 0 && targetPage === normalizedExpandedTarget;
		if (!animate) {
			child.classList.toggle(STEP_OPEN_CLASS, shouldOpen);
			panel.hidden = !shouldOpen;
			panel.style.height = '';
			if (toggleButton instanceof HTMLButtonElement) {
				toggleButton.setAttribute('aria-expanded', shouldOpen ? 'true' : 'false');
			}
			return;
		}

		if (shouldOpen) {
			openPrimaryStepPanel(child, panel, toggleButton);
			return;
		}

		closePrimaryStepPanel(child, panel, toggleButton);
	});
}

/**
* Responsibility:
* - Handle grouped primary-step chevron toggles without disturbing the current page state.
*
* Touches:
* - Updates the remembered accordion state for the current primary-step context.
* - Applies one-open-at-a-time accordion state to the currently rendered sidebar items.
*
* Guarantees:
* - Chevron clicks expand/collapse only and never trigger GF page navigation.
*/
export function handlePrimaryToggleClick(formId, listElement, event, toggleButton) {
	if (!(toggleButton instanceof HTMLButtonElement) || !(listElement instanceof HTMLElement)) {
		return;
	}

	const primaryItem = toggleButton.closest(`.${STEP_GROUP_CLASS}`);
	if (!(primaryItem instanceof HTMLElement)) {
		return;
	}

	event.preventDefault();
	event.stopPropagation();

	const targetPage = resolveRenderedPrimaryTargetPage(primaryItem);
	if (targetPage <= 0) {
		return;
	}

	const currentlyExpandedTarget = readPrimaryAccordionState(formId).expandedTargetPage;
	const nextExpandedTarget = currentlyExpandedTarget === targetPage ? 0 : targetPage;
	writePrimaryAccordionState(formId, {
		expandedTargetPage: nextExpandedTarget,
		currentPrimaryTargetPage: resolveRenderedCurrentPrimaryTargetPage(listElement),
		manualSelection: true,
	});
	applyPrimaryStepExpansion(listElement, formId, nextExpandedTarget, {
		animate: true,
	});
}
